import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { Observable, of, tap } from 'rxjs';
import {
  ChangeMyPasswordDto,
  ForgotMyPasswordDto,
  ForgotMyPasswordResponseDto,
  LoginDto,
  LoginResponseDto,
  RefreshTokenResponseDto,
  RegistrationDto,
  RegistrationResponseDto,
  ValidateEmailForChangePassword,
} from 'src/app/data/dtos';
import { AppStateInterface } from 'src/app/store/types';
import { environment } from 'src/environments/environment';
import * as HomeActions from '../../store/home/actions';
import * as JobsActions from '../../store/jobs/actions';
import * as NotificationsActions from '../../store/notifications/actions';
import * as ProfileActions from '../../store/profile/actions';
import * as SettingsActions from '../../store/settings/actions';
import * as UnseenNotificationsActions from '../../store/unseen-notifications/actions';
import { DashboardService } from '../dashboard/dashboard.service';
import { BrowserService } from '../browser.service';


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private router: Router,
    private httpClient: HttpClient,
    private cookieService: CookieService,
    private _store: Store<AppStateInterface>,
    private readonly browserService: BrowserService,
  ) {}

  public emailConfirmed: boolean = false;
  public tokenNotFound: boolean = false;

  login(loginDto: LoginDto): Observable<LoginResponseDto> {
    return this.httpClient.post<LoginResponseDto>(
      `${environment.api_url}/login`,
      loginDto,
      { withCredentials: true },
    );
  }

  sendNewConfirmationEmail() {
    this.httpClient
      .post(
        `${environment.api_url}/newConfirmationEmail`,
        {},
        {
          withCredentials: true,
        },
      )
      .subscribe({
        next: (response) => {},
        error: (error) => {},
      });
  }

  register(registrationDto: RegistrationDto): Observable<RegistrationResponseDto> {
    return this.httpClient.post<RegistrationResponseDto>(
      `${environment.api_url}/employee-registration`,
      registrationDto,
    );
  }

  confirmUserEmail(confirmationDigits: string, token: string) {
    return this.httpClient
      .post(`${environment.api_url}/confirmation/${token}`, {
        confirmationDigits: confirmationDigits
      })
  }

  sendForgotMyPassword(forgotMyPasswordDto: ForgotMyPasswordDto): Observable<ForgotMyPasswordResponseDto>  {
    return this.httpClient.post<ForgotMyPasswordResponseDto>(`${environment.api_url}/forgotmypassword`, forgotMyPasswordDto)
  }

  sendForgotMyPasswordByToken(token: string): Observable<ForgotMyPasswordResponseDto>  {
    return this.httpClient.post<ForgotMyPasswordResponseDto>(`${environment.api_url}/forgotmypasswordByToken/${token}`, {})
  }

  validateUserEmailForChangePassword(confirmationDigits: string, token: string){
    return this.httpClient.post<ValidateEmailForChangePassword>(`${environment.api_url}/forgotmypassword/validate/${token}`, {confirmationDigits: confirmationDigits})
  }

  changeMyPassword(token: string, changeMyPasswordDto: ChangeMyPasswordDto) {
    return this.httpClient
      .post(
        `${environment.api_url}/auth/changemypassword/${token}`,
        changeMyPasswordDto,
      )
  }

  refreshToken(): Observable<RefreshTokenResponseDto> {
    const refreshToken = this.browserService.localStorageGetItem('RefreshToken');
    if (refreshToken) {
      return this.httpClient.post<RefreshTokenResponseDto>(
        `${environment.api_url}/refresh`,
        {
          RefreshToken: refreshToken,
        },
        {
          withCredentials: true,
        },
      );
    } else {
      this.browserService.localStorageSetItem('authenticated', 'false');
      this.router.navigate(['auth/login']);
      return of();
    }
  }

  logout(redirectToLogin: boolean) {
    const authenticated = this.browserService.localStorageGetItem('authenticated');
    const accessToken = this.cookieService.get('AccessToken');
    const activeLanguage = this.browserService.localStorageGetItem('active-language')
    if (accessToken && authenticated) {
      return this.httpClient
        .post(`${environment.api_url}/logout`, {}, { withCredentials: true })
        .pipe(
          tap(() => {
            this.removeOverwrittenBackgroundWhiteTheme();
          })
        )
        .subscribe({
          next: (success) => {
            this.removeAuthCredentialsFromLocalStorage();
            this.resetAllGlobalStates()
            if (redirectToLogin) {
              this.router.navigate(['/auth/login']);
            } else {
              if (redirectToLogin) {
                switch(activeLanguage){
                  case 'en':
                    this.router.navigate(['/en']);
                    break
                  case 'de':
                    this.router.navigate(['/de']);
                    break
                  case 'fa':
                    this.router.navigate(['/fa']);
                    break
                  case 'id':
                    this.router.navigate(['/id']);
                    break
                  default:
                    this.router.navigate(['/en']);
                }
              }
            }
          },
          error: (error) => {
            this.removeAuthCredentialsFromLocalStorage();
            this.resetAllGlobalStates()
            if (redirectToLogin) {
              this.router.navigate(['/auth/login']);
            } else {
              switch(activeLanguage){
                case 'en':
                  this.router.navigate(['/en']);
                  break
                case 'de':
                  this.router.navigate(['/de']);
                  break
                case 'fa':
                  this.router.navigate(['/fa']);
                  break
                case 'id':
                  this.router.navigate(['/id'])
                  break
                default:
                  this.router.navigate(['/en']);
              }
            }
          },
        });
    } else {
      this.removeAuthCredentialsFromLocalStorage();
      this.removeOverwrittenBackgroundWhiteTheme();
      this.resetAllGlobalStates()
      if (redirectToLogin) {
        this.router.navigate(['/auth/login']);
      } else {
        switch(activeLanguage){
          case 'en':
            this.router.navigate(['/en']);
            break;
          case 'de':
            this.router.navigate(['/de']);
            break;
          case 'fa':
            this.router.navigate(['/fa']);
            break;
          case 'id':
            this.router.navigate(['/id']);
            break;
          default:
            this.router.navigate(['/en']);
        }
      }
      return of(null);
    }
  }

  setDirection(language: string) {
    const browserDocument = this.browserService.document;
    if(browserDocument){
      if (language === 'fa') {
        const elements = browserDocument.querySelectorAll('[dir="ltr"]');
        // input elements must alwas be ltr since we dont allow writing text with rtl directions
        const filteredElements = Array.from(elements).filter(el => {
          return el.tagName.toLowerCase() !== 'input' && el.tagName.toLowerCase() !== 'textarea'}
        );
        filteredElements.forEach((element) => {
          element.setAttribute('dir', 'rtl');
        });
      } else {
        const elements = browserDocument.querySelectorAll('[dir="rtl"]');
        // input elements must alwas be ltr since we dont allow writing text with rtl directions
        const filteredElements = Array.from(elements).filter(el => {
          return el.tagName.toLowerCase() !== 'input' && el.tagName.toLowerCase() !== 'textarea' }
        )
        filteredElements.forEach((element) => {
          element.setAttribute('dir', 'ltr');
        });
      }
    }
  }

  private removeAuthCredentialsFromLocalStorage() {
    this.browserService.localStorageSetItem('authenticated', 'false');
    this.browserService.localStorageRemoveItem('RefreshToken');
  }

  getLanguageDirection(language: string){
    if(language === 'fa'){
      return 'rtl'
    } else {
      return 'ltr'
    }
  }

  /**
   * This method checks if the given string is not empty null or undefined
   * white space is still considered empty string
   *
   * @param str the given string to be checked
   * @returns boolean wheter the string is not empty null or undefined
   * @example undefined -> false
   *          null -> false"" -> false
   *          " " -> false
   *          "1" -> true
   *          "2 " -> true
   *          " 3 3" -> true
   */
  isNotEmptyNullOrUndefined(str: string | null | undefined): boolean {
    if(str != null && str != undefined) {
      return str.toString().trim().length > 0;
    }
    return false
  }

  resetAllGlobalStates(){
    this._store.dispatch(HomeActions.resetHomeData())
    this._store.dispatch(JobsActions.resetJobsData())
    this._store.dispatch(JobsActions.resetJobFavoritesData())
    this._store.dispatch(NotificationsActions.resetNotificationsData())
    this._store.dispatch(ProfileActions.resetProfileData())
    // this._store.dispatch(ProfileActions.resetAvatar())
    this._store.dispatch(SettingsActions.resetSettingsData())
    this._store.dispatch(UnseenNotificationsActions.restUnseenNotificationsData())
  }

  getActiveLanguage(): string {
    let cachedSelectedLanguage = this.browserService.localStorageGetItem('active-language');
    if (cachedSelectedLanguage && cachedSelectedLanguage != null) {
      return cachedSelectedLanguage;
    }
    this.browserService.localStorageSetItem('active-language', 'en');
    return 'en';
  }

  private removeOverwrittenBackgroundWhiteTheme(){
    const browserDocument = this.browserService.document;
    if(browserDocument){
      browserDocument.body.classList.remove('bg-gray-200')
    }
  }
}
